import type { CragRoute } from '../../../types/crag-route';
import { routeNeighbours } from './route-neighbours';

/** Metres. Matches `Route::MAX_LENGTH_METERS` — the server refuses more. */
export const MAX_ROUTE_LENGTH = 1000;

/**
 * The metres field as the server wants it: whole metres, or nothing.
 *
 * The draft holds what was typed rather than a number, because a half-typed
 * field is a string and clearing it has to stay distinguishable from zero.
 * `invalid` is the third answer — the input is `type="number"`, so it is mostly
 * reachable by pasting, but it must not be silently saved as null.
 */
export function parseRouteLength(typed: string): { value: number | null; invalid: boolean } {
  return parseWholeNumber(typed, MAX_ROUTE_LENGTH);
}

/** A positive whole number up to `max`, or nothing, or `invalid`. */
export function parseWholeNumber(typed: string, max: number): { value: number | null; invalid: boolean } {
  const trimmed = typed.trim();
  if (trimmed === '') return { value: null, invalid: false };

  const value = Number(trimmed);
  const valid = Number.isInteger(value) && value > 0 && value <= max;

  return valid ? { value, invalid: false } : { value: null, invalid: true };
}

/** A neighbour's figure, and which hand it is on. */
export interface NearbyRouteNumber {
  side: 'left' | 'right';
  value: number;
}

/** What `pick` reads off the routes immediately either side, where they have it. */
export function nearbyRouteNumbers(
  routes: CragRoute[],
  selectedRouteId: string | null,
  pick: (route: CragRoute) => number | null | undefined
): NearbyRouteNumber[] {
  const { left, right } = routeNeighbours(routes, selectedRouteId);

  const neighbours = [
    { side: 'left' as const, route: left },
    { side: 'right' as const, route: right },
  ];

  return neighbours.flatMap(({ side, route }) => {
    const value = route ? pick(route) : null;

    return typeof value === 'number' && value > 0 ? [{ side, value }] : [];
  });
}

/**
 * One neighbour's length, and which hand it is on.
 *
 * The side is what makes the suggestion checkable: "30m" alone has to be taken
 * on trust, while "SX 30m" can be read against the cliff in front of you.
 */
export interface NearbyRouteLength {
  side: 'left' | 'right';
  length: number;
}

/**
 * The lengths of the two routes immediately either side of this one.
 *
 * Sectors are listed in the order they are walked past, so a route's neighbours
 * in that list are its neighbours on the cliff — and a cliff is as tall as it
 * is: the line next to a 25m route is almost always 25m too. That makes the
 * neighbours' lengths a better first guess than any average of the crag, and
 * saves the measurement being retyped for every line on the same wall.
 *
 * **Strictly adjacent**, rather than the nearest measured lines wherever they
 * are: the guess is only worth making while it is the same piece of rock. Walk
 * far enough along a sector and the wall changes height, so a length borrowed
 * from the far end is a worse suggestion than no suggestion — it would be typed
 * in without being checked. An unmeasured neighbour therefore offers nothing
 * rather than passing the turn to the line beyond it.
 *
 * Two neighbours of the same height stay two suggestions rather than collapsing
 * into one — with a side on each, they are no longer the same statement.
 */
export function nearbyRouteLengths(
  routes: CragRoute[],
  selectedRouteId: string | null
): NearbyRouteLength[] {
  return nearbyRouteNumbers(routes, selectedRouteId, (route) => route.length).map(({ side, value }) => ({
    side,
    length: value,
  }));
}
